import { apiCall, isBackendConfigured } from './api-call'
import { isDevAuthBypassEnabled } from './dev-auth-bypass'
import { readImportedListings, writeImportedListings } from './local-store'
import { ImportedListing } from '../_types/imported-listing'
import { defaultImportedListing } from '../_defaults/imported-listing'
import { buildSeedImportedListings } from './imported-listings-seed-data'

const warnFallback = (operation: string, error: unknown): void => {
  console.warn(`[imported-listings] backend ${operation} failed —`, error)
}

/**
 * Reads imported listings from local storage. In dev-bypass mode, seeds
 * a representative list on first read so the UI has something to render.
 * Real Firebase Auth users only ever see what's actually in storage —
 * never seed data.
 *
 * @param {string} quickPassUserId - the QuickPass user whose listings to read
 * @returns {ImportedListing[]} the locally stored listings, newest first
 */
const readLocalImportedListings = (
  quickPassUserId: string,
): ImportedListing[] => {
  const existing = readImportedListings(quickPassUserId)

  if (existing.length > 0) {
    return existing
  }

  if (isDevAuthBypassEnabled() === false) {
    return []
  }

  const seed = buildSeedImportedListings(quickPassUserId)
  writeImportedListings(quickPassUserId, seed)
  return seed
}

/**
 * Returns all imported listings for the renter, newest first. In dev-
 * bypass mode reads from the seeded local store; otherwise hits the
 * backend and returns an empty list on failure (never fakes data for
 * real users).
 *
 * @param {string} quickPassUserId - the QuickPass user whose listings to load
 * @returns {Promise<ImportedListing[]>} the listing array
 */
const getImportedListingsForUser = async (
  quickPassUserId: string,
): Promise<ImportedListing[]> => {
  if (isBackendConfigured() === false) {
    return readLocalImportedListings(quickPassUserId)
  }

  try {
    const response = await apiCall({
      group: 'quickpass',
      command: 'getimportedlistings',
      data: { quickPassUserId },
    })

    if (response.code !== 200) {
      throw new Error(response.message)
    }

    return JSON.parse(response.data) as ImportedListing[]
  } catch (error) {
    warnFallback('getImportedListings', error)
    return []
  }
}

/**
 * Asks the backend to scrape a listing URL and save it. Falls back to a
 * local-only stub when the backend is unreachable — this fallback ONLY
 * stores the bare URL because we cannot scrape from the browser due to
 * CORS, so the renter sees a placeholder until backend comes back.
 *
 * @param {string} quickPassUserId
 * @param {string} url
 * @returns {Promise<ImportedListing>}
 */
const importListingFromUrl = async (
  quickPassUserId: string,
  url: string,
): Promise<ImportedListing> => {
  if (isBackendConfigured() === true) {
    try {
      const response = await apiCall({
        group: 'quickpass',
        command: 'importlistingfromurl',
        data: { quickPassUserId, url },
      })

      if (response.code !== 200) {
        throw new Error(response.message)
      }

      const listing = JSON.parse(response.data) as ImportedListing

      // Persist to local store too so reads stay fast and offline-friendly
      const existing = readImportedListings(quickPassUserId)
      const filtered = existing.filter(entry => entry.id !== listing.id)
      writeImportedListings(quickPassUserId, [listing, ...filtered])

      return listing
    } catch (error) {
      warnFallback('importListingFromUrl', error)
    }
  }

  const now = Date.now()
  let parsedHostname = ''
  try {
    parsedHostname = new URL(url).hostname
  } catch {
    parsedHostname = ''
  }

  const stub: ImportedListing = {
    ...defaultImportedListing,
    id: `local_${now}_${Math.random().toString(36).slice(2, 8)}`,
    quickPassUserId,
    sourceUrl: url,
    sourceSite: 'unknown',
    title:
      parsedHostname !== ''
        ? `Listing on ${parsedHostname}`
        : 'Imported listing',
    scrapeStatus: 'failed',
    scrapeNotes:
      'Backend is unreachable, so we just stored the URL. Refresh once the api-server-gateway is back online to pull real details.',
    lastScrapedAt: now,
    createdAt: now,
    updatedAt: now,
  }

  const existing = readImportedListings(quickPassUserId)
  writeImportedListings(quickPassUserId, [stub, ...existing])

  return stub
}

/**
 * Removes an imported listing.
 *
 * @param {string} quickPassUserId
 * @param {string} listingId
 * @returns {Promise<void>}
 */
const removeImportedListing = async (
  quickPassUserId: string,
  listingId: string,
): Promise<void> => {
  if (isBackendConfigured() === true) {
    try {
      const response = await apiCall({
        group: 'quickpass',
        command: 'removeimportedlisting',
        data: { quickPassUserId, listingId },
      })

      if (response.code !== 200 && response.code !== 404) {
        throw new Error(response.message)
      }
    } catch (error) {
      warnFallback('removeImportedListing', error)
    }
  }

  const existing = readImportedListings(quickPassUserId)
  writeImportedListings(
    quickPassUserId,
    existing.filter(entry => entry.id !== listingId),
  )
}

/**
 * Triggers a fresh scrape of an existing imported listing.
 *
 * @param {string} quickPassUserId
 * @param {string} listingId
 * @returns {Promise<ImportedListing | null>}
 */
const refreshImportedListing = async (
  quickPassUserId: string,
  listingId: string,
): Promise<ImportedListing | null> => {
  if (isBackendConfigured() === true) {
    try {
      const response = await apiCall({
        group: 'quickpass',
        command: 'refreshimportedlisting',
        data: { quickPassUserId, listingId },
      })

      if (response.code !== 200) {
        throw new Error(response.message)
      }

      const refreshed = JSON.parse(response.data) as ImportedListing
      const existing = readImportedListings(quickPassUserId)
      const next = existing.map(entry =>
        entry.id === listingId ? refreshed : entry,
      )
      writeImportedListings(quickPassUserId, next)

      return refreshed
    } catch (error) {
      warnFallback('refreshImportedListing', error)
    }
  }

  return (
    readImportedListings(quickPassUserId).find(
      entry => entry.id === listingId,
    ) ?? null
  )
}

export {
  getImportedListingsForUser,
  importListingFromUrl,
  removeImportedListing,
  refreshImportedListing,
}
